<script setup lang="ts">

import ItemV3ParameterBox from "@/components/itemV3/parameter/itemV3ParameterBox.vue";
import {computed, inject, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";
import {useI18n} from "vue-i18n";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import {getCurrentText} from "@/assets/ts/itemText";

const {t} = useI18n();

const emit = defineEmits(["patch"]);
const props = defineProps(["item"]);

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

const imageWidthPercent: number = 30
const loading = ref(true)

/**
 * Parameter box header images
 */

const mainMetal = computed(() => store.getters["session/getMainMetal"])
const boxIdx = "color"

const getHeaderImage = (size: string = "600x300", withBg = false) => {
  if (withBg) {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${mainMetal.value}_${boxIdx}.webp`
  } else {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${boxIdx}.webp`
  }
}

/**
 * session
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const variantIdx = "discs.*.color"

const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

const currentDiscsColorText = computed(() => {
  return getCurrentText(configureAll.value ? -1 : itemIdx.value, variantIdx)
})

const open = ref(false)

const discVariants = computed(() => open.value ? parametersVariations.getVariants() : [])

/**
 * color variants
 */

const activeDiscIdx = ref(0)

const discSliderStyle = computed(() => {
  const count = discVariants.value.length;
  if (count === 0 || activeDiscIdx.value < 0 || activeDiscIdx.value >= count) return {display: 'none'};

  const width = 100 / count;
  const left = activeDiscIdx.value * width;

  return {
    width: `calc(${width}% - 4px)`,
    left: `calc(${left}% + 2px)`,
  };
});

const colorVariantsOfDisc = computed(() => {
  if (!discVariants.value.length || discVariants.value.length < activeDiscIdx.value) return []
  return discVariants.value[activeDiscIdx.value].values
})

const activeDisc = computed(() => {
  if (!discVariants.value.length || discVariants.value.length < activeDiscIdx.value) return []
  return discVariants.value[activeDiscIdx.value]
})

const matchedDiscIdx = computed(() => {
  if (!discVariants.value.length || discVariants.value.length < activeDiscIdx.value) return -1
  return discVariants.value[activeDiscIdx.value].values.findIndex((v: any) => v.match)
})

/**
 * image
 */

const hoverColorIdx = ref(-1)

const setHoverColorIdx = () => {
  if (matchedDiscIdx.value !== -1 && colorVariantsOfDisc.value.length >= matchedDiscIdx.value) {
    hoverColorIdx.value = matchedDiscIdx.value
  }
}

watch(activeDisc, () => {
  setHoverColorIdx()
})

watch(props.item, () => {
  setHoverColorIdx()
})

const imageUrl = computed(() => parametersVariations.imageUrl(colorVariantsOfDisc.value, hoverColorIdx.value))

const getColorImageUrl = (color: string, size: number) => {
  return 'https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/colors/' + size + 'x' + size + '/' + color.toLowerCase() + '.webp'
}

const checkImageLoaded = (url: string) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(true);
    img.src = url;
  });
};

const checkAllLoaded = async () => {
  const discs = discVariants.value;
  if (!discs.length) return;

  const allListsCreated = discs.every((disc: any) => disc.values && disc.values.length > 0);
  if (!allListsCreated) return;

  const imageUrls = discs.flatMap((disc: any) => disc.values.map((color: any) => getColorImageUrl(color.idx, 300)));
  await Promise.all(imageUrls.map((url: string) => checkImageLoaded(url)));

  loading.value = false;
};

watch(discVariants, () => {
  checkAllLoaded();
  if (discVariants.value.length > 0) {
    loading.value = true;
  }
}, {immediate: true, deep: true});

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  emit('patch', {
    action: actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};

/**
 * tip texts
 */

const getColorTipTitle = computed(() => {
  if (activeDisc.value && activeDisc.value.values[hoverColorIdx.value]) {
    const key = activeDisc.value.values[hoverColorIdx.value].additional_value.toUpperCase() + "-" + activeDisc.value.values[hoverColorIdx.value].idx.toUpperCase()
    return t('components.parameters.tips.colorMetals.' + key + '.title')
  } else {
    return ''
  }
})

const getColorTipText = computed(() => {
  if (activeDisc.value && activeDisc.value.values[hoverColorIdx.value]) {
    const key = activeDisc.value.values[hoverColorIdx.value].additional_value.toUpperCase() + "-" + activeDisc.value.values[hoverColorIdx.value].idx.toUpperCase()
    return t('components.parameters.tips.colorMetals.' + key + '.text')
  } else {
    return ''
  }
})

const getColorTipImageUrl = computed(() => {
  if (activeDisc.value && activeDisc.value.values[hoverColorIdx.value]) {
    const key = activeDisc.value.values[hoverColorIdx.value].additional_value.toUpperCase() + "-" + activeDisc.value.values[hoverColorIdx.value].idx.toUpperCase()
    return "https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/tips/metal/300x300/" + key + ".webp"

  } else {
    return ''
  }
})

onMounted(() => {
      setHoverColorIdx()
    }
)

</script>

<template>
  <item-v3-parameter-box
      v-model:open="open"
      :image-width-percent=imageWidthPercent
      :image=imageUrl
      :header-image="getHeaderImage()"
      :header-image-srcset="getHeaderImage() + ' 300w, ' + getHeaderImage('1200x600') +' 600w'"
      title="Farbe"
      :subtitle="currentDiscsColorText || 'Wählen Sie die Metalfarbe für Ihre Trauringe'"
      :loading=loading
      :tip-title="getColorTipTitle"
      :tip-text="getColorTipText"
      :tip-image="getColorTipImageUrl"
  >
    <template v-slot:main>

      <nav v-if="discVariants.length > 1" class="w-full mb-4">
        <!-- disc selection -->
        <div
            class="relative flex items-center w-full h-[22px] max-h-[22px] bg-[#f3f0e9] rounded-full p-[2px] border border-[#e8e2d8] select-none">
          <!-- Sliding Background -->
          <div
              class="absolute top-[2px] bottom-[2px] bg-brand-main rounded-full transition-all duration-300 ease-in-out shadow-xs pointer-events-none"
              :style="discSliderStyle"
          ></div>

          <!-- Disc Buttons -->
          <button
              v-for="(disc, discIdx) in discVariants"
              :key="discIdx"
              type="button"
              class=" text-[10px] tracking-wided relative flex-1 flex cursor-pointer items-center justify-center text-xs transition-colors duration-200 z-10 font-normal leading-none h-full"
              :class="activeDiscIdx === discIdx ? 'text-white font-medium' : 'text-gray-500 hover:text-gray-700'"
              @click="activeDiscIdx = discIdx"
          >
            Scheibe {{ discIdx + 1 }}
          </button>
        </div>
      </nav>
      <ul class="flex flex-wrap w-full justify-center ">
        <!-- disc list -->
        <li v-for="(color, colorIdx) in colorVariantsOfDisc" :key="colorIdx"
            class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]"
            @click="handleValuePatch(activeDisc.idx, color)"
            @mouseover="hoverColorIdx = colorIdx as number"
            @mouseleave="hoverColorIdx = matchedDiscIdx"
            :class="{
              'border-2 border-brand-main bg-[#f3f0e9]': color.match,
              'border-2 border-transparent': !color.match
            }"
        >
          <img
              :src="getColorImageUrl(color.idx, 300)"
              :srcset="getColorImageUrl(color.idx, 600)"
              class="w-10 aspect-square"
          >
          <span class="text-parameter-text text-[11px]">
            {{ color.title }}
          </span>
        </li>
      </ul>
    </template>
  </item-v3-parameter-box>
</template>

<style scoped>

</style>